'use client';

// ===================================================
// GAM Command Center — Editor overlay layer
// One place that decides where the editor's floating
// surfaces live in the stacking order.
// ===================================================

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Above every app chrome layer measured on 2026-09-06.
 * The ceiling that day was 10020 — the fullscreen embedded-app frame
 * (src/components/embedded-apps/EmbeddedAppFrame.tsx) — with the design
 * capture layer and the icon picker just below it at 10002/10003.
 * The editor's own surfaces used to sit at 50 and 60, which is why other
 * panels covered the text toolbar and the slash quick bar.
 */
/**
 * The editor's rung on the ONE app-wide layer scale.
 *
 * Same number as `Z.popover` in src/lib/ui/layers.ts and `--z-popover` in
 * globals.css. Duplicated rather than imported because this package must not
 * depend on the app that consumes it — if you change one, change all three.
 */
export const EDITOR_OVERLAY_Z = 10040;

const EditorOverlayLayer = createContext(EDITOR_OVERLAY_Z);

/** The host chooses a layer above itself, from its own shared layer scale. */
export function EditorOverlayProvider({ zIndex, children }: { zIndex: number; children: ReactNode }) {
  return <EditorOverlayLayer.Provider value={zIndex}>{children}</EditorOverlayLayer.Provider>;
}

/**
 * Renders children into document.body.
 *
 * A high z-index alone is not enough: an ancestor with `transform`,
 * `filter` or `will-change` creates a stacking context, and a child can
 * never escape it however large its z-index. The sidebar and several
 * panels animate with `transform`, so the only reliable fix is to leave
 * the subtree entirely.
 *
 * Nothing renders on the server, and nothing renders on the very first
 * client paint — `document.body` does not exist during SSR.
 */
export function EditorOverlay({ children }: { children: ReactNode }) {
  const zIndex = useContext(EditorOverlayLayer);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return createPortal(
    <div data-editor-overlay style={{ position: 'fixed', inset: 0, zIndex, pointerEvents: 'none' }}>
      <div style={{ pointerEvents: 'auto' }}>{children}</div>
    </div>,
    document.body,
  );
}
